import React from "react";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";

function formatNumber(value, format) {
  switch (format) {
    case "dollars":
      return `$${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    case "percentage":
      return `${(value * 100).toFixed(2)}%`;
    case "number":
      return `${parseFloat(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    default:
      return value.toFixed(2);
  }
}

function getMetricLabel(metricValue, metrics) {
  if (!metricValue || !metrics || !Array.isArray(metrics)) {
    return metricValue || "Unknown";
  }

  let baseValue = metricValue;
  let suffix = "";

  if (metricValue.endsWith("_previous_year")) {
    baseValue = metricValue.replace("_previous_year", "");
    suffix = " (Prev Year)";
  }

  const metric = metrics.find((m) => m && m.value === baseValue);
  if (metric && metric.label) {
    return `${metric.label}${suffix}`;
  }
  return metricValue;
}

const COLORS = [
  "#8884d8", // Purple
  "#82ca9d", // Green
  "#ffc658", // Yellow
  "#ff7300", // Orange
  "#d0ed57", // Light Green
  "#a4de6c", // Lighter Green
  "#ff69b4", // Pink
  "#8dd1e1", // Light Blue
  "#83a6ed", // Lighter Blue
  "#888888", // Gray
];

const PieChart = ({
  activeTab,
  intradayData,
  data,
  rawData,
  selectedMetrics,
  metrics,
  compareLocations,
  compareDow,
  isDashboard,
  summaryData,
  locationData,
  chartHeight,
  compactAxis,
}) => {
  // In compact mode, show just the percent inside each slice and use a Legend
  // to identify slices. Default mode keeps labels rendered outside.
  const sliceLabel = compactAxis
    ? ({ percent }) => `${(percent * 100).toFixed(0)}%`
    : ({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`;
  const sliceLabelInside = compactAxis;
  const pieOuterRadius = compactAxis ? "70%" : 80;
  const safeMetrics = metrics || [];
  const safeSelectedMetrics = selectedMetrics || [];
  const safeData = data || [];
  const safeSummaryData = summaryData || [];
  const safeLocationData = locationData || [];

  const firstMetric = safeMetrics.find((m) => m && m.value === safeSelectedMetrics[0]);
  const formatType = firstMetric && firstMetric.format ? firstMetric.format : "number";

  const renderChart = () => {
    // Safety variables
    const safeIntradayData = intradayData || [];

    // For pie charts, we want to show comparison data
    if (compareDow && activeTab === "Intraday" && safeIntradayData.length > 0) {
      // Show day of week comparison for intraday data
      const dayTotals = safeIntradayData.reduce((acc, row) => {
        if (!row || !row.day_of_week || !safeSelectedMetrics[0]) return acc;
        const day = row.day_of_week;
        const value = row[safeSelectedMetrics[0]] || 0;
        acc[day] = (acc[day] || 0) + value;
        return acc;
      }, {});

      const pieData = Object.entries(dayTotals).map(([day, total]) => ({
        name: day,
        value: total,
        label: day,
      }));

      return (
        <RechartsPieChart width="100%" height={400}>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={sliceLabel}
            {...(sliceLabelInside ? { labelPosition: "inside" } : {})}
            outerRadius={pieOuterRadius}
            fill="#8884d8"
            dataKey="value"
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [formatNumber(value, formatType), "Value"]}
          />
          {compactAxis && <Legend verticalAlign="bottom" height={24} iconSize={10} />}
        </RechartsPieChart>
      );
    } else if (compareLocations && safeLocationData.length > 0) {
      // Show locations/employees comparison
      const pieData = safeLocationData.map((item, index) => ({
        name: item.location || item.employee || item.role,
        value: item.total || item.average || 0,
        label: item.location || item.employee || item.role,
      }));

      return (
        <RechartsPieChart width="100%" height={400}>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={sliceLabel}
            {...(sliceLabelInside ? { labelPosition: "inside" } : {})}
            outerRadius={pieOuterRadius}
            fill="#8884d8"
            dataKey="value"
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [formatNumber(value, formatType), "Value"]}
          />
          {compactAxis && <Legend verticalAlign="bottom" height={24} iconSize={10} />}
        </RechartsPieChart>
      );
    }

    // For multiple metrics comparison
    if (safeSelectedMetrics.length > 1 && safeSummaryData.length > 0) {
      const pieData = safeSummaryData.map((metric, index) => ({
        name: getMetricLabel(metric.metric, safeMetrics),
        value: metric.total || metric.average || 0,
        label: getMetricLabel(metric.metric, safeMetrics),
      }));

      return (
        <RechartsPieChart width="100%" height={400}>
          <Pie
            data={pieData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={sliceLabel}
            {...(sliceLabelInside ? { labelPosition: "inside" } : {})}
            outerRadius={pieOuterRadius}
            fill="#8884d8"
            dataKey="value"
          >
            {pieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [formatNumber(value, formatType), "Value"]}
          />
          {compactAxis && <Legend verticalAlign="bottom" height={24} iconSize={10} />}
        </RechartsPieChart>
      );
    }

    // For single metric over time, show top periods
    if (safeData.length > 0) {
      const topData = safeData
        .map((item) => ({
          name: item.date || `Period ${safeData.indexOf(item) + 1}`,
          value: item[safeSelectedMetrics[0]] || 0,
        }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 8); // Show top 8 periods

      return (
        <RechartsPieChart width="100%" height={400}>
          <Pie
            data={topData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={sliceLabel}
            {...(sliceLabelInside ? { labelPosition: "inside" } : {})}
            outerRadius={pieOuterRadius}
            fill="#8884d8"
            dataKey="value"
          >
            {topData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            formatter={(value) => [formatNumber(value, formatType), getMetricLabel(safeSelectedMetrics[0], safeMetrics)]}
          />
          {compactAxis && <Legend verticalAlign="bottom" height={24} iconSize={10} />}
        </RechartsPieChart>
      );
    }

    // Fallback: No data message
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-gray-500">No data available for pie chart</p>
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={chartHeight ?? (isDashboard ? 300 : 400)}>
      {renderChart()}
    </ResponsiveContainer>
  );
};

export default PieChart;
